import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import type { Intl } from "../../../../shared/core/i18n/use-intl";
import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { transactionSearchManager } from "../../../../shared/core/service/services";
import { getSettlementBalance } from "../../../../shared/domains/account/account";
import { useAccounts } from "../../../../shared/domains/account/use-accounts";
import { TimeRange } from "../../../../shared/domains/accounting-transaction/search/timerange";
import { JustificationFilter } from "../../../../shared/domains/accounting-transaction/search/transaction-search-manager";
import type { AccountingTransaction } from "../../../../shared/domains/accounting-transaction/transaction/transaction";
import { TransactionDirection } from "../../../../shared/domains/accounting-transaction/transaction/transaction-filter";
import { useTransactionFilter } from "../../../../shared/domains/accounting-transaction/transaction/use-transaction-filter";
import { useTransactions } from "../../../../shared/domains/accounting-transaction/transaction/use-transactions";
import { useLoadMoreDebounce } from "../../../../shared/utils/list";
import { useObservable } from "../../../../shared/utils/observable";
import { fetchPeriod } from "../../../../shared/utils/period-fetcher";
import { ACCOUNT_TRANSACTION_DETAILS_MODAL_ID } from "../../../core/modal/modal-id";
import { UnstyledButton } from "../../common/buttons/default-button";
import { TertiaryButton } from "../../common/buttons/tertiary-button";
import { CloseIcon } from "../../common/close-icon";
import { Modal } from "../../common/modal/modal";
import { PageBackHeader } from "../../common/page/page-back-header";
import { SegmentedControl } from "../../common/segmented-control";
import { MainColorSpinner } from "../../common/spinner";
import { FiltersIcon } from "../../common/svg/filters-icon";
import { SearchIcon } from "../../common/svg/search-icon";
import { theme } from "../../styles/theme";
import { AccountTransactionDetailsModal } from "./components/accounting-transaction-details-modal";
import { TransactionRow } from "./components/transaction-row";
import { TransactionsFilterDrawer } from "./components/transactions-filter-drawer";
import { useSelectedAccount } from "./utils/use-selected-account";

export default function AllTransactionsScreen() {
  const accounts = useAccounts();
  const { goBack } = useHistory();
  const transactionDirection = useObservable(transactionSearchManager.directionSelection);
  const timerangeSelection = useObservable(transactionSearchManager.timeRangeSelection);
  const [startDate, endDate] = timerangeSelection.bounds;
  const [startPeriodLabel, setStartPeriodLabel] = useState<string>();
  const [nextPeriodLabel, setNextPeriodLabel] = useState<string>();
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState("");
  const [focusedAccount] = useSelectedAccount(accounts);
  const { filters, formattedAmountRange, hasAmountFilter, justificationFilter } = useTransactionFilter(focusedAccount);
  const transactions = useTransactions(focusedAccount, transactionDirection, filters);

  const intl = useIntl();
  const { formatMessage, formatDate } = intl;

  const searchedTransactions = useMemo(() => {
    if (!search || !transactions.data) {
      return transactions.data;
    }
    const needle = search.toLowerCase();
    return transactions.data.filter(
      (transaction) =>
        transaction.type?.label?.toLowerCase().includes(needle) ||
        transaction.shortDescription?.toLowerCase().includes(needle) ||
        transaction.description?.toLowerCase().includes(needle),
    );
  }, [transactions.data, search]);

  const sections = useMemo(() => {
    if (transactions.loading || !searchedTransactions) {
      return [];
    }
    const transactionsGroupedByDay = groupTransactionsByDay(intl, searchedTransactions);
    return Object.values(transactionsGroupedByDay).sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [searchedTransactions, transactions.loading, intl]);

  const loadMoreDebounce = useLoadMoreDebounce();
  const loadMore = useCallback(
    () =>
      loadMoreDebounce(
        transactions.loadMore,
        transactions.loading || transactions.loadingMore,
        transactions.canLoadMore,
      ),
    [transactions, loadMoreDebounce],
  );

  const fetchPreviousPeriod = () => {
    fetchPeriod("previous", startDate ?? moment(), endDate ?? moment(), transactionSearchManager);
  };

  const fetchNextPeriod = () => {
    fetchPeriod("next", startDate ?? moment(), endDate ?? moment(), transactionSearchManager);
  };

  useEffect(() => {
    if (startDate) {
      setStartPeriodLabel(
        `${moment(startDate)?.subtract(3, "M").format("DD/MM/YYYY")} → ${moment(startDate)
          ?.subtract(1, "days")
          .format("DD/MM/YYYY")}`,
      );
    }
  }, [startDate]);

  useEffect(() => {
    if (endDate) {
      setNextPeriodLabel(
        `${moment(endDate)?.add(1, "days").format("DD/MM/YYYY")} → ${moment(endDate)
          ?.add(3, "M")
          .add(1, "days")
          .format("DD/MM/YYYY")}`,
      );
    }
  }, [endDate]);

  const isNextPeriodInTheFuture = () => moment(endDate).add(1, "day").startOf("day").isAfter();

  const showTransactionDetails = (transaction: AccountingTransaction) => {
    focusedAccount &&
      Modal.present(
        ACCOUNT_TRANSACTION_DETAILS_MODAL_ID,
        () => <AccountTransactionDetailsModal transaction={transaction} account={focusedAccount} />,
        { canBeDismissed: false },
      );
  };

  if (!focusedAccount) {
    return null;
  }

  const hasCustomPeriod = timerangeSelection.timeRange === TimeRange.Other;
  /** One removable chip per filter the drawer holds, so what narrows the list stays in sight. */
  const chips = [
    justificationFilter !== JustificationFilter.ALL && {
      key: "justification",
      label: formatMessage(
        justificationFilter === JustificationFilter.TO_JUSTIFY
          ? "transactionFilter.toJustify"
          : "transactionFilter.toQualify",
      ),
      clear: () => transactionSearchManager.setJustificationFilter(JustificationFilter.ALL),
    },
    hasAmountFilter && {
      key: "amount",
      label: formattedAmountRange,
      clear: () => transactionSearchManager.setAmountRange(null, null),
    },
    hasCustomPeriod && {
      key: "period",
      label: [startDate, endDate]
        .filter(Boolean)
        .map((bound) => formatDate(bound!.toDate(), { day: "2-digit", month: "2-digit", year: "numeric" }))
        .join(" → "),
      clear: () => transactionSearchManager.setTimeRange(TimeRange.Quarter),
    },
  ].filter(Boolean) as { key: string; label: string; clear: () => void }[];

  const directions = [
    { value: TransactionDirection.ALL, label: formatMessage("transactionFilter.all") },
    { value: TransactionDirection.OUTCOME, label: formatMessage("transactionFilter.debit") },
    { value: TransactionDirection.INCOME, label: formatMessage("transactionFilter.credit") },
  ];

  return (
    <>
      <PageBackHeader
        title={formatMessage("allTransactions.title")}
        subtitle={formatMessage("allTransactions.subtitle")}
        account={focusedAccount}
        onBack={goBack}
      />

      <Controls>
        <SegmentedControl
          options={directions}
          value={transactionDirection}
          onChange={(direction) => transactionSearchManager.setTransactionDirection(direction)}
        />
        <RightControls>
          <SearchField>
            <SearchGlyph width={18} height={18} fill={theme.colors.gray[500]} />
            <SearchInput
              type="search"
              value={search}
              placeholder={formatMessage("transactionFilter.searchInputPlaceholder")}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchField>
          <FiltersButton
            type="button"
            aria-label={formatMessage("transactionFilter.filtersButton")}
            onClick={() => setShowFilters(true)}
          >
            <FiltersIcon />
          </FiltersButton>
        </RightControls>
      </Controls>

      {chips.length > 0 && (
        <Chips>
          {chips.map((chip) => (
            <Chip key={chip.key} type="button" onClick={chip.clear}>
              {chip.label}
              <ChipCross />
            </Chip>
          ))}
        </Chips>
      )}

      {transactions.loading && transactions.data.length === 0 ? (
        <Loader key="spinner" />
      ) : searchedTransactions.length === 0 ? (
        <EmptyLabel>{formatMessage("accountTransactions.emptyTransactionList")}</EmptyLabel>
      ) : (
        <StyledInfiniteScroll
          loadMore={loadMore}
          hasMore={transactions.canLoadMore}
          loader={<Loader key="transaction-loader" />}
        >
          {sections.map((section) => (
            <Section key={section.label}>
              <SectionTitle>{section.label}</SectionTitle>
              {section.data.map((transaction) => (
                <TransactionRow
                  key={transaction.id}
                  transaction={transaction}
                  onClick={() => showTransactionDetails(transaction)}
                />
              ))}
            </Section>
          ))}
        </StyledInfiniteScroll>
      )}

      {!transactions.canLoadMore && !transactions.loading && (
        <PeriodButtons>
          <PeriodButton>
            <TertiaryButton onClick={fetchPreviousPeriod}>
              {formatMessage("accountTransactions.previousPeriodButtonLabel")}
            </TertiaryButton>
            {!!startPeriodLabel && <PeriodLabel>{startPeriodLabel}</PeriodLabel>}
          </PeriodButton>
          {!isNextPeriodInTheFuture() && (
            <PeriodButton>
              <TertiaryButton onClick={fetchNextPeriod}>
                {formatMessage("accountTransactions.nextPeriodButtonLabel")}
              </TertiaryButton>
              {!!nextPeriodLabel && <PeriodLabel>{nextPeriodLabel}</PeriodLabel>}
            </PeriodButton>
          )}
        </PeriodButtons>
      )}

      {showFilters && (
        <TransactionsFilterDrawer
          currencyId={getSettlementBalance(focusedAccount)?.currencyAlpha ?? focusedAccount.currencyAlpha}
          toJustifyCount={transactions.unjustifiedTransactions2.total}
          toQualifyCount={transactions.unqualifiedTransactions.total}
          onClose={() => setShowFilters(false)}
        />
      )}
    </>
  );
}

function groupTransactionsByDay(intl: Intl, transactions: AccountingTransaction[]) {
  return transactions.reduce<{
    [key: string]: {
      label: string;
      data: AccountingTransaction[];
      date: string;
    };
  }>((groups, transaction) => {
    const dateGroup = intl.formatDate(transaction.date, { year: "numeric", month: "numeric", day: "numeric" });
    if (groups[dateGroup]) {
      groups[dateGroup].data.push(transaction);
    } else {
      groups[dateGroup] = {
        label: intl.formatRelativeDate(transaction.date),
        date: transaction.date,
        data: [transaction],
      };
    }
    return groups;
  }, {});
}

const Controls = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  margin-bottom: 16px;
`;

/* The field and the button travel together, against the right edge. */
const RightControls = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`;

const SearchField = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 320px;
  max-width: 100%;
  padding: 0 16px;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 12px;
  background-color: #ffffff;
`;

const SearchGlyph = styled(SearchIcon)`
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  flex: 1;
  height: 42px;
  border: 0;
  outline: none;
  background-color: transparent;
  ${theme.text};
  font-size: 14px;
  color: ${theme.colors.blue[800]};

  &::placeholder {
    color: ${theme.colors.gray[500]};
  }
`;

const FiltersButton = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 12px;
  background-color: #ffffff;
  color: ${theme.colors.blue[800]};
`;

/* Ferried to the right, under the search field they belong to. */
const Chips = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  align-self: stretch;
  margin-bottom: 20px;
`;

const Chip = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 16px;
  background-color: #ffffff;
  cursor: pointer;
  ${theme.text};
  font-size: 13px;
  color: ${theme.colors.blue[800]};
`;

const ChipCross = styled(CloseIcon)`
  width: 12px;
  height: 12px;
  flex-shrink: 0;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  margin-bottom: 8px;
`;

const SectionTitle = styled.h2`
  ${theme.text};
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 18px;
  color: ${theme.colors.gray[800]};
`;

const StyledInfiniteScroll = styled(InfiniteScroll)`
  align-self: stretch;
  width: 100%;
`;

const Loader = styled(MainColorSpinner)`
  align-self: center;
  margin: 24px 0;
`;

const EmptyLabel = styled.span`
  ${theme.text};
  color: ${theme.colors.gray[500]};
`;

const PeriodButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-self: stretch;
  gap: 16px;
  margin-top: 16px;
`;

const PeriodButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PeriodLabel = styled.span`
  ${theme.text};
  margin-top: 4px;
  font-size: 12px;
  color: ${theme.colors.gray[500]};
`;
